import { FeaturedContent } from 'rwl-shared/dist/models/neighborhood';
import { getSafeTimestamp } from './reviewSortUtils';

type Job = FeaturedContent['jobs'][number];

/**
 * Compare jobs by showcase status (showcase first)
 */
export const compareByShowcase = (a: Job, b: Job): number => {
    return (b.showcase ? 1 : 0) - (a.showcase ? 1 : 0);
};

/**
 * Compare jobs by whether they have photos (with photos first)
 */
export const compareByHasPhotos = (a: Job, b: Job): number => {
    const aHasPhotos = (a.media?.length ?? 0) > 0 ? 1 : 0;
    const bHasPhotos = (b.media?.length ?? 0) > 0 ? 1 : 0;
    return bHasPhotos - aHasPhotos;
};

/**
 * Compare jobs by whether they have a review (with review first)
 */
export const compareByHasReview = (a: Job, b: Job): number => {
    return (b.reviewId ? 1 : 0) - (a.reviewId ? 1 : 0);
};

/**
 * Compare jobs by date (most recent first, preferring completedAt over startedAt)
 */
export const compareByDate = (a: Job, b: Job): number => {
    const aTime = getSafeTimestamp(a.completedAt ?? a.startedAt);
    const bTime = getSafeTimestamp(b.completedAt ?? b.startedAt);
    return bTime - aTime;
};

/**
 * Sort jobs by: 1) showcase desc, 2) has photos, 3) has review, 4) most recent date desc
 */
export const sortJobs = (
    jobs: FeaturedContent['jobs']
): FeaturedContent['jobs'] => {
    return [...jobs].sort((a, b) => {
        // 1. Showcase first
        const showcaseDiff = compareByShowcase(a, b);
        if (showcaseDiff !== 0) return showcaseDiff;

        // 2. Has photos first
        const photosDiff = compareByHasPhotos(a, b);
        if (photosDiff !== 0) return photosDiff;

        // 3. Has review first
        const reviewDiff = compareByHasReview(a, b);
        if (reviewDiff !== 0) return reviewDiff;

        // 4. Most recent date descending
        return compareByDate(a, b);
    });
};
